import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PaperClipIcon } from '@heroicons/react/24/outline';
import {
  useAdminMaintenanceInfaqs,
  useAdminApproveInfaq,
  useAdminRejectInfaq,
  useAdminApproveClosing,
  useAdminRejectClosing,
} from '../../../hooks/useMaintenanceInfaq';
import type { MaintenanceInfaq } from '../../../services/maintenance-infaq.service';

type Tab = 'pending' | 'closing';

export default function MaintenanceInfaqApprovalPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('pending');
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  // Pending review query
  const pendingQ = useAdminMaintenanceInfaqs('PENDING_REVIEW');
  // Closing review: status CLOSED + closingStatus SUBMITTED. The adminListAll
  // filters by status only, so we further filter on the client.
  const closedQ = useAdminMaintenanceInfaqs('CLOSED');

  const approveMutation = useAdminApproveInfaq();
  const rejectMutation = useAdminRejectInfaq();
  const approveClosingMutation = useAdminApproveClosing();
  const rejectClosingMutation = useAdminRejectClosing();

  const pendingList = pendingQ.data ?? [];
  const closingList = (closedQ.data ?? []).filter(
    (c) => c.closingStatus === 'SUBMITTED',
  );

  const submitReject = async () => {
    if (!rejectingId || !rejectReason.trim()) return;
    if (tab === 'closing') {
      await rejectClosingMutation.mutateAsync({
        id: rejectingId,
        reason: rejectReason.trim(),
      });
    } else {
      await rejectMutation.mutateAsync({
        id: rejectingId,
        reason: rejectReason.trim(),
      });
    }
    setRejectingId(null);
    setRejectReason('');
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      <header>
        <h1 className="text-2xl font-semibold text-neutral-900">
          {t('maintenanceInfaq.adminTitle', { defaultValue: 'Maintenance Infaq Approvals' })}
        </h1>
        <p className="mt-1 text-sm text-neutral-600">
          {t('maintenanceInfaq.adminSubtitle', {
            defaultValue:
              'Review provider campaigns and approve closing reports for fund release.',
          })}
        </p>
      </header>

      <div className="flex gap-2 border-b border-neutral-200">
        <TabButton
          active={tab === 'pending'}
          onClick={() => setTab('pending')}
          count={pendingList.length}
        >
          Pending approval
        </TabButton>
        <TabButton
          active={tab === 'closing'}
          onClick={() => setTab('closing')}
          count={closingList.length}
        >
          Closing review
        </TabButton>
      </div>

      {tab === 'pending' ? (
        pendingQ.isLoading ? (
          <Loading />
        ) : pendingList.length === 0 ? (
          <Empty text="No campaigns awaiting approval." />
        ) : (
          <div className="space-y-4">
            {pendingList.map((c) => (
              <CampaignCard
                key={c.id}
                campaign={c}
                actionButtons={
                  <>
                    <button
                      onClick={() => approveMutation.mutate(c.id)}
                      disabled={approveMutation.isPending}
                      className="rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => {
                        setRejectingId(c.id);
                        setRejectReason('');
                      }}
                      className="rounded-md border border-red-300 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50"
                    >
                      Reject
                    </button>
                  </>
                }
              />
            ))}
          </div>
        )
      ) : closedQ.isLoading ? (
        <Loading />
      ) : closingList.length === 0 ? (
        <Empty text="No closing reports awaiting review." />
      ) : (
        <div className="space-y-4">
          {closingList.map((c) => (
            <CampaignCard
              key={c.id}
              campaign={c}
              closingReport
              actionButtons={
                <>
                  <button
                    onClick={() => approveClosingMutation.mutate(c.id)}
                    disabled={approveClosingMutation.isPending}
                    className="rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
                  >
                    Approve & disburse
                  </button>
                  <button
                    onClick={() => {
                      setRejectingId(c.id);
                      setRejectReason('');
                    }}
                    className="rounded-md border border-red-300 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50"
                  >
                    Reject
                  </button>
                </>
              }
            />
          ))}
        </div>
      )}

      {rejectingId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
            <h3 className="text-lg font-semibold text-neutral-900">Reject campaign</h3>
            <p className="mt-1 text-xs text-neutral-500">
              A reason is required. The provider will be able to address and resubmit.
            </p>
            <textarea
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="mt-3 w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
              placeholder="Reason for rejection…"
            />
            <div className="mt-4 flex justify-end gap-3">
              <button
                onClick={() => setRejectingId(null)}
                className="rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
              >
                Cancel
              </button>
              <button
                onClick={submitReject}
                disabled={!rejectReason.trim() || rejectMutation.isPending || rejectClosingMutation.isPending}
                className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-60"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  count,
  children,
}: {
  active: boolean;
  onClick: () => void;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${
        active
          ? 'border-primary-600 text-primary-700'
          : 'border-transparent text-neutral-500 hover:text-neutral-800'
      }`}
    >
      {children}
      {count > 0 && (
        <span className="ml-2 inline-block rounded-full bg-neutral-200 px-2 py-0.5 text-xs text-neutral-700">
          {count}
        </span>
      )}
    </button>
  );
}

function CampaignCard({
  campaign,
  actionButtons,
  closingReport,
}: {
  campaign: MaintenanceInfaq;
  actionButtons: React.ReactNode;
  closingReport?: boolean;
}) {
  const raised = Number(campaign.raisedAmount || 0);
  const target = Number(campaign.targetAmount || 0);
  const report: any = closingReport ? campaign.closingReport : null;

  return (
    <div className="rounded-md border border-neutral-200 bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-neutral-900">{campaign.title}</h3>
            <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-700">
              {campaign.status.replace(/_/g, ' ').toLowerCase()}
            </span>
          </div>
          <p className="mt-1 text-sm text-neutral-700">{campaign.reason}</p>
          <p className="mt-1 text-xs text-neutral-500">
            by {campaign.provider?.companyName ?? '—'} · target RM {target.toFixed(2)}
            {closingReport && (
              <> · raised RM {raised.toFixed(2)}</>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">{actionButtons}</div>
      </div>

      <div className="mt-3 border-t border-neutral-100 pt-3 text-sm text-neutral-700">
        <p className="whitespace-pre-wrap">{campaign.description}</p>
      </div>

      {(campaign.damagePhotoUrls as string[] | undefined)?.length ? (
        <Gallery
          label="Damage photos"
          urls={campaign.damagePhotoUrls as string[]}
        />
      ) : null}

      {(campaign.submissionInvoiceUrls as string[] | undefined)?.length ? (
        <Links
          label="Submission invoices"
          urls={campaign.submissionInvoiceUrls as string[]}
        />
      ) : null}

      {closingReport && report && (
        <div className="mt-3 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm">
          <div className="mb-1 text-xs font-semibold uppercase text-blue-900">
            Closing report
          </div>
          <div className="space-y-1 text-blue-900">
            <div>
              Actual cost: <strong>RM {Number(report.actualCost ?? 0).toFixed(2)}</strong>
            </div>
            <div className="whitespace-pre-wrap">{report.workDescription}</div>
            {report.notes && <div className="text-xs italic">Notes: {report.notes}</div>}
            {Array.isArray(report.receiptUrls) && report.receiptUrls.length > 0 && (
              <Links label="Receipts" urls={report.receiptUrls} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Gallery({ label, urls }: { label: string; urls: string[] }) {
  return (
    <div className="mt-3">
      <div className="mb-1 text-xs font-semibold uppercase text-neutral-500">{label}</div>
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
        {urls.map((u, i) => (
          <a
            key={i}
            href={u}
            target="_blank"
            rel="noreferrer"
            className="block overflow-hidden rounded-md border border-neutral-200"
          >
            <img src={u} alt={`${label}-${i}`} className="h-20 w-full object-cover" />
          </a>
        ))}
      </div>
    </div>
  );
}

function Links({ label, urls }: { label: string; urls: string[] }) {
  return (
    <div className="mt-3">
      <div className="mb-1 text-xs font-semibold uppercase text-neutral-500">{label}</div>
      <ul className="space-y-1 text-xs">
        {urls.map((u, i) => (
          <li key={i}>
            <a
              href={u}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-primary-700 hover:underline"
            >
              <PaperClipIcon className="h-3 w-3" /> Document {i + 1}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Loading() {
  return (
    <div className="rounded-md border border-neutral-200 bg-white p-8 text-center text-sm text-neutral-500">
      Loading…
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return (
    <div className="rounded-md border border-dashed border-neutral-300 bg-white p-12 text-center text-sm text-neutral-600">
      {text}
    </div>
  );
}
